import { FC } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';

import { useExamplePreview } from 'hooks/useExamplePreview';

import { ReadOnlyTableRow } from 'components/BusinessToolForm/Sections/FinancialPlan/components/TableComponents/ReadOnlyTableRow';
import { TableSectionTitle } from 'components/BusinessToolForm/Sections/FinancialPlan/components/TableComponents/TableSectionTitle';
import { getEachRowValues } from 'components/BusinessToolForm/Sections/FinancialPlan/ProfitAndLossProjection/components/FormSections/NetSalesForm/helpers';
import { GLOBAL_PERIOD_NAME } from 'components/BusinessToolForm/Sections/Global/form';

import { concatWithDot } from 'helpers/string';

import { OPERATING_ASSETS_AND_LIABILITIES_NAMES, useGetOperatingAssetsAndLiabilitiesLinkedFields } from './utils/form';
import { FIRST_COLUMN_WIDTH, operatingAssetsAndLiabilitiesPrefix } from '../../../../utils/form';

export const OperatingAssetsAndLiabilitiesForm: FC = () => {
  const { t } = useTranslation();
  const {isExamplePage} = useExamplePreview()

  const { getValues } = useFormContext();
  const operatingAssetsAndLiabilitiesLinkedFields = useGetOperatingAssetsAndLiabilitiesLinkedFields(isExamplePage)

  const period = useWatch({ name: GLOBAL_PERIOD_NAME });

  const [accountsReceivable, inventories, accountsPayable, otherAssets] = getValues([
    concatWithDot(
      operatingAssetsAndLiabilitiesPrefix,
      OPERATING_ASSETS_AND_LIABILITIES_NAMES.INCREASE_DECREASE_ACCOUNTS_RECEIVABLE,
    ),
    concatWithDot(
      operatingAssetsAndLiabilitiesPrefix,
      OPERATING_ASSETS_AND_LIABILITIES_NAMES.INCREASE_DECREASE_INVENTORIES,
    ),
    concatWithDot(
      operatingAssetsAndLiabilitiesPrefix,
      OPERATING_ASSETS_AND_LIABILITIES_NAMES.INCREASE_DECREASE_ACCOUNTS_PAYABLE,
    ),
    concatWithDot(
      operatingAssetsAndLiabilitiesPrefix,
      OPERATING_ASSETS_AND_LIABILITIES_NAMES.INCREASE_DECREASE_OTHER_OPERATING_ASSETS_LIABILITIES,
    ),
  ]);

  return (
    <Grid container direction={'column'}>
      <Grid item xs>
        <TableSectionTitle title={t('financialPlan.projectedCashFlow.inDirect.operatingAssetsAndLiabilities.title')} />
      </Grid>
      <Grid item xs>
        <ReadOnlyTableRow
          name={concatWithDot(
            operatingAssetsAndLiabilitiesPrefix,
            OPERATING_ASSETS_AND_LIABILITIES_NAMES.INCREASE_DECREASE_ACCOUNTS_RECEIVABLE,
          )}
          columnValues={getEachRowValues([accountsReceivable], 0, period, false, true)}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          rowTitle={
            operatingAssetsAndLiabilitiesLinkedFields[
              OPERATING_ASSETS_AND_LIABILITIES_NAMES.INCREASE_DECREASE_ACCOUNTS_RECEIVABLE
            ].title
          }
          noNeedName={true}
        />
      </Grid>
      <Grid item xs>
        <ReadOnlyTableRow
          name={concatWithDot(
            operatingAssetsAndLiabilitiesPrefix,
            OPERATING_ASSETS_AND_LIABILITIES_NAMES.INCREASE_DECREASE_INVENTORIES,
          )}
          columnValues={getEachRowValues([inventories], 0, period, false, true)}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          rowTitle={
            operatingAssetsAndLiabilitiesLinkedFields[
              OPERATING_ASSETS_AND_LIABILITIES_NAMES.INCREASE_DECREASE_INVENTORIES
            ].title
          }
          noNeedName={true}
        />
      </Grid>
      <Grid item xs>
        <ReadOnlyTableRow
          name={concatWithDot(
            operatingAssetsAndLiabilitiesPrefix,
            OPERATING_ASSETS_AND_LIABILITIES_NAMES.INCREASE_DECREASE_ACCOUNTS_PAYABLE,
          )}
          columnValues={getEachRowValues([accountsPayable], 0, period, false, false)}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          rowTitle={
            operatingAssetsAndLiabilitiesLinkedFields[
              OPERATING_ASSETS_AND_LIABILITIES_NAMES.INCREASE_DECREASE_ACCOUNTS_PAYABLE
            ].title
          }
          noNeedName={true}
        />
      </Grid>
      <Grid item xs>
        <ReadOnlyTableRow
          name={concatWithDot(
            operatingAssetsAndLiabilitiesPrefix,
            OPERATING_ASSETS_AND_LIABILITIES_NAMES.INCREASE_DECREASE_OTHER_OPERATING_ASSETS_LIABILITIES,
          )}
          columnValues={getEachRowValues([otherAssets], 0, period, false, true)}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          rowTitle={
            operatingAssetsAndLiabilitiesLinkedFields[
              OPERATING_ASSETS_AND_LIABILITIES_NAMES.INCREASE_DECREASE_OTHER_OPERATING_ASSETS_LIABILITIES
            ].title
          }
          noNeedName={true}
        />
      </Grid>
    </Grid>
  );
};
